import styles from './contacts-page.module.scss';
import React, { useEffect, useState, useRef } from 'react';
import TextBox from '../../atoms/Base/TextBox/TextBox';
import ContactsTableTemplate from '../../templates/ContactsTableTemplate/ContactsTableTemplate';
import Label from '../../atoms/Base/Label/Label';
import { StringHelper } from '../../../common/helpers/string-helper';
import { useContactsApiService } from '../../../api/providers/AppServiceProvider';
import { useAppDispatch } from '../../../redux/hooks';
import { setCachedTotalContactCount } from '../../../features/contacts-slice';
import { StringValueCallback } from '../../../types/Callbacks';
import { Container } from '../../atoms/Base/Container/Container';
import CXDataLoader from '../../atoms/Custom/CXDataLoader/CXDataLoader';
import { usePageTracking } from '../../../hooks/usePageTracking';

export default function ContactsPage() {
	const dispatch = useAppDispatch();
	const contactsApiService = useContactsApiService();
	
	usePageTracking('Page Viewed', 'Contacts');

	const [totalContactCount, setTotalContactCount] = useState<number>(0);
	const [searchTerm, setSearchTerm] = useState<string>(StringHelper.Empty);
	const [isLoadingCount, setIsLoadingCount] = useState<boolean>(true);
	const [isLoadingContacts, setIsLoadingContacts] = useState<boolean>(false);
	const hasLoadedRef = useRef(false);

	// Only show main loader if count is loading, let table handle its own loading
	const isLoading = isLoadingCount;

	const onChangeHandler: StringValueCallback = (newValue: string) => {
		setSearchTerm(newValue.toLowerCase());
	};

	const handleContactsLoadingChange = (loading: boolean) => {
		setIsLoadingContacts(loading);
	};

	useEffect(() => {
		// Only load once
		if (hasLoadedRef.current) {
			return;
		}

		const loadContactCount = async () => {
			try {
				setIsLoadingCount(true);
				const response = await contactsApiService.GetTotalCount();
				dispatch(setCachedTotalContactCount(response.count));
				setTotalContactCount(response.count);
				hasLoadedRef.current = true;
			} catch (error) {
				console.error('Error loading contact count:', error);
			} finally {
				setIsLoadingCount(false);
			}
		};

		loadContactCount();
	}, [dispatch, contactsApiService]);

	return (
		<div>
			<Container className='p-4'>
				<div className={styles.contacts_list_container}>
					<div className={styles.title}>
						<Label>Contacts List</Label>
					</div>
					<div className={styles.search}>
						<TextBox placeHolder={'Search'} showSearchIcon={true} onChange={onChangeHandler} />
					</div>
					{!isLoading && totalContactCount > 0 && (
						<div className={styles.record_number}>
							<Label>Total number of contacts ({totalContactCount})</Label>
						</div>
					)}
				</div>
				<Container topSpaceLarge>
					{isLoading ? (
						<div className="flex items-center justify-center min-h-[400px] w-full">
							<CXDataLoader />
						</div>
					) : (
						<ContactsTableTemplate 
							searchTerm={searchTerm} 
							isDefault 
							paging 
							onLoadingChange={handleContactsLoadingChange}
						/>
					)}
				</Container>
			</Container>
		</div>
	);
}
